import { useState, useEffect, useRef } from 'react';
import { Bell, CheckCheck, ArrowRight, Check, X, Pencil } from 'lucide-react';
import { vzhledModulu, cilNotifikace, jeAkcniNotifikace, cestaRozhodnuti, popiskyRozhodnuti, upravitelnaNepritomnost, barvaTypu } from '../lib/notifikace';
import NotifikaceKarta from './notifikace/Karta';
import UpravitNepritomnost from './notifikace/UpravitNepritomnost';

const SECTION_COLOR = '#FF8A00';

// ── Helpers ───────────────────────────────────────────────────
function relativeTime(iso) {
    if (!iso) return '';
    const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (min < 1)  return 'teď';
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24)   return `${h} h`;
    const d = Math.floor(h / 24);
    if (d < 30)   return `${d} d`;
    return `${Math.floor(d / 30)} měs.`;
}

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

// ── Skeleton ──────────────────────────────────────────────────
function Skeleton({ isDark }) {
    const bg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
    return (
        <div className="flex items-center gap-2.5 px-3 py-2.5 animate-pulse">
            <div className="w-10 h-10 rounded-full shrink-0" style={{ background: bg }} />
            <div className="flex-1 space-y-1.5">
                <div className="h-3 rounded w-3/4" style={{ background: bg }} />
                <div className="h-2.5 rounded w-1/2" style={{ background: bg }} />
            </div>
        </div>
    );
}

// ── Hlavní komponenta ─────────────────────────────────────────
export default function NotifikaceDropdown({ open, onClose, onNavigate, isDark, triggerRef, panelStyle, user }) {
    const [notifikace, setNotifikace] = useState([]);
    const [loading, setLoading] = useState(true);
    const panelRef = useRef(null);
    // Notifikace, jejíž nepřítomnost se právě upravuje před schválením. Formulář
    // je v portálu mimo panel — klik do něj nebo Escape nesmí zavřít zvoneček
    // (a s ním i rozepsaný formulář).
    const [upravuje, setUpravuje] = useState(null);
    const upravujeRef = useRef(null);
    upravujeRef.current = upravuje;

    // Plocha jako malý chat a karty: neutrální `--surface-raised` + stín modalu.
    // Dřív vlastní modrošedé rgba(15,15,25) — vedle zbytku aplikace cizí.
    const borderClr = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const hover = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';

    // Fetch jen nepřečtené při otevření; otevřený seznam obnoví nová nebo
    // jinde vyřízená notifikace (app.jsx → `notifikace-seznam`)
    useEffect(() => {
        if (!open) return;
        const nacist = (tise) => {
            if (!tise) setLoading(true);
            fetch('/api/notifikace?limit=20&neprecten=1')
                .then(r => r.json())
                .then(result => {
                    setNotifikace(result.data || []);
                    setLoading(false);
                })
                .catch(() => setLoading(false));
        };
        nacist(false);
        const obnovit = () => nacist(true);
        window.addEventListener('notifikace-seznam', obnovit);
        return () => window.removeEventListener('notifikace-seznam', obnovit);
    }, [open]);

    // Klik mimo panel → zavřít (ignoruje klik na trigger button)
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (upravujeRef.current) return;
            if (panelRef.current && !panelRef.current.contains(e.target)
                && !(triggerRef?.current && triggerRef.current.contains(e.target))) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open, onClose, triggerRef]);

    // Escape → zavřít
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === 'Escape' && !upravujeRef.current) onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    if (!open) return null;

    const markAllRead = () => {
        fetch('/api/notifikace/precist-vse', { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf() } })
            .then(() => {
                setNotifikace([]);
                window.dispatchEvent(new Event('notifikace-changed'));
            });
    };

    const handleItemClick = (n) => {
        fetch(`/api/notifikace/${n.id}/precist`, { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf() } })
            .then(() => window.dispatchEvent(new Event('notifikace-changed')));
        setNotifikace(prev => prev.filter(x => x.id !== n.id));
        const cil = cilNotifikace(n, user);
        if (cil) onNavigate(cil.page, cil.params);
        onClose();
    };

    const handleRozhodnuti = async (n, souhlas) => {
        const path = cestaRozhodnuti(n, souhlas);
        if (!path) return;
        await fetch(path, { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf() } });
        await fetch(`/api/notifikace/${n.id}/precist`, { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf() } });
        setNotifikace(prev => prev.filter(x => x.id !== n.id));
        window.dispatchEvent(new Event('notifikace-changed'));
    };

    return (
        <div
            ref={panelRef}
            className="fixed z-50 flex flex-col overflow-hidden"
            style={{
                ...(panelStyle ?? { top: 60, right: 24 }),
                width: 400,
                maxHeight: 'min(560px, calc(100vh - 80px))',
                borderRadius: 16,
                background: 'var(--surface-raised)',
                border: `1px solid ${borderClr}`,
                boxShadow: 'var(--modal-shadow)',
            }}
        >
            {/* ── Hlavička — stejná skladba jako malý chat ── */}
            <div className="flex items-center gap-2 shrink-0"
                style={{ padding: '10px 10px 10px 14px', borderBottom: `1px solid ${borderClr}` }}>
                <Bell size={17} style={{ color: SECTION_COLOR }} />
                <span className="flex-1 font-semibold" style={{ color: 'var(--text-primary)', fontSize: 14 }}>
                    Notifikace
                    {notifikace.length > 0 && (
                        <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
                            · {notifikace.length} nepřečtených
                        </span>
                    )}
                </span>
                {notifikace.length > 0 && (
                    <button
                        type="button"
                        onClick={markAllRead}
                        className="flex items-center gap-1.5 rounded-lg transition-colors"
                        style={{ height: 30, padding: '0 10px', fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer' }}
                        onMouseEnter={e => { e.currentTarget.style.background = hover; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                    >
                        <CheckCheck size={15} /> Přečíst vše
                    </button>
                )}
            </div>

            {/* ── Seznam ── */}
            <div className="flex-1 overflow-y-auto" style={{ minHeight: 0, padding: 6 }}>
                {loading ? (
                    <>
                        <Skeleton isDark={isDark} />
                        <Skeleton isDark={isDark} />
                        <Skeleton isDark={isDark} />
                    </>
                ) : notifikace.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-2">
                        <Bell size={32} style={{ color: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)' }} />
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            Žádné nepřečtené notifikace
                        </span>
                    </div>
                ) : (
                    notifikace.map(n => {
                        const mod = vzhledModulu(n.modul);
                        const jeAkcni = jeAkcniNotifikace(n, user);
                        const popisky = popiskyRozhodnuti(n);
                        const akce = jeAkcni ? [
                            { id: 'ano', label: popisky.ano, Icon: Check, ton: 'ok', onClick: () => handleRozhodnuti(n, true) },
                            { id: 'ne', label: popisky.ne, Icon: X, ton: 'ne', onClick: () => handleRozhodnuti(n, false) },
                            ...(upravitelnaNepritomnost(n)
                                ? [{ id: 'upravit', label: 'Upravit', Icon: Pencil, onClick: () => setUpravuje(n) }] : []),
                        ] : [];

                        return (
                            <div key={n.id} className="rounded-xl transition-colors"
                                onMouseEnter={e => { e.currentTarget.style.background = hover; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                            <NotifikaceKarta
                                varianta="radek"
                                isDark={isDark}
                                Ikona={mod.Icon}
                                barva={mod.color}
                                stitek={mod.label}
                                cas={relativeTime(n.created_at)}
                                typBarva={barvaTypu(n.typ)}
                                titulek={n.titulek}
                                zprava={n.zprava}
                                neprectena={!n.precten}
                                akce={akce}
                                naKliknuti={jeAkcni ? null : () => handleItemClick(n)}
                            />
                            </div>
                        );
                    })
                )}
            </div>

            {/* ── Footer — odkaz na plnou stránku ── */}
            <button
                onClick={() => { onNavigate('aktivita'); onClose(); }}
                className="w-full flex items-center justify-center gap-1.5 shrink-0 transition-colors"
                style={{
                    height: 42, fontSize: 13, fontWeight: 600,
                    color: 'var(--text-secondary)',
                    background: 'none',
                    border: 'none',
                    borderTop: `1px solid ${borderClr}`,
                    cursor: 'pointer',
                }}
                onMouseEnter={e => e.currentTarget.style.background = hover}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
                Zobrazit všechny notifikace <ArrowRight size={13} />
            </button>

            {upravuje && (
                <UpravitNepritomnost
                    nedostupnostId={upravitelnaNepritomnost(upravuje)}
                    isDark={isDark}
                    onClose={() => setUpravuje(null)}
                    onHotovo={async () => {
                        const n = upravuje;
                        setUpravuje(null);
                        await fetch(`/api/notifikace/${n.id}/precist`, { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf() } });
                        setNotifikace(prev => prev.filter(x => x.id !== n.id));
                        window.dispatchEvent(new Event('notifikace-changed'));
                    }}
                />
            )}
        </div>
    );
}
